import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { formatMoney, maskMoneyInput, parseInputToApi } from '@/lib/money';
import { Field } from '@/features/transactions/TransactionForm';
import { formatRate } from './types';
import type { ComparisonResult, ProposalResult } from './types';

type Draft = {
  label: string;
  amount: string;
  ratePercent: string;
  installments: string;
  system: string;
  iof: string;
  fees: string;
};

const EMPTY: Draft = {
  label: '', amount: '', ratePercent: '', installments: '', system: 'price', iof: '', fees: '',
};

/** RF-DIV-10 — prazos padrão em um clique. */
const QUICK_TERMS = [12, 24, 36, 48, 60, 72];

/**
 * Comparador de propostas — RF-DIV-09, RN-69, RN-70.
 *
 * A TELA NUNCA MOSTRA A PARCELA SOZINHA. Cada proposta sai com parcela, prazo,
 * total pago, CET e valor presente na mesma linha. O vendedor compara pela
 * parcela porque e o unico numero em que a proposta pior costuma ganhar; aqui
 * os cinco aparecem juntos, e a proposta com parcela menor e total maior recebe
 * um aviso em vermelho com os dois valores na frase.
 */
export function SimulatorPanel({ selectedIds }: { selectedIds: number[] }) {
  const [drafts, setDrafts] = useState<Draft[]>([
    { ...EMPTY, label: 'Proposta A' },
    { ...EMPTY, label: 'Proposta B' },
  ]);

  const simulate = useMutation({
    mutationFn: () =>
      api.post<{ data: ComparisonResult }>('/debts/simulate', {
        proposals: drafts.map((d) => ({
          label: d.label || undefined,
          requested_amount: parseInputToApi(d.amount),
          interest_rate_monthly_percent: Number(d.ratePercent.replace(',', '.')),
          installments_count: Number(d.installments),
          amortization_system: d.system,
          ...(d.iof ? { iof_amount: parseInputToApi(d.iof) } : {}),
          ...(d.fees ? { fees_amount: parseInputToApi(d.fees) } : {}),
        })),
      }, { entityIds: selectedIds }),
    onSuccess: () => undefined,
  });

  const error = simulate.error instanceof ApiError ? simulate.error : null;
  const result = simulate.data?.data;

  function patch(index: number, changes: Partial<Draft>) {
    setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...changes } : d)));
  }

  const complete = drafts.every(
    (d) => d.amount !== '' && d.ratePercent !== '' && Number(d.installments) > 0,
  );

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm dark:border-indigo-900/50 dark:bg-indigo-950/30">
        <p className="font-medium text-indigo-900 dark:text-indigo-200">
          Comparar pela parcela é como se perde dinheiro aqui.
        </p>
        <p className="mt-1 text-indigo-800/80 dark:text-indigo-300/80">
          Alongar o prazo derruba a parcela e aumenta o total pago. Esta tela mostra parcela, total,
          CET e valor presente lado a lado — e avisa quando a parcela menor sai mais cara.
        </p>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        {drafts.map((draft, index) => (
          <div
            key={index}
            className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
          >
            <div className="mb-3 flex items-center gap-2">
              <input
                value={draft.label}
                onChange={(e) => patch(index, { label: e.target.value })}
                placeholder={`Proposta ${index + 1}`}
                className="input flex-1 font-medium"
              />
              {drafts.length > 2 && (
                <button
                  onClick={() => setDrafts((prev) => prev.filter((_, i) => i !== index))}
                  className="rounded-lg px-2 py-1 text-xs text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40"
                >
                  remover
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Valor" required>
                <input
                  value={draft.amount}
                  onChange={(e) => patch(index, { amount: maskMoneyInput(e.target.value) })}
                  inputMode="numeric"
                  placeholder="0,00"
                  className="input w-full text-right"
                />
              </Field>
              <Field label="Taxa % a.m." required hint="como está no contrato">
                <input
                  value={draft.ratePercent}
                  onChange={(e) =>
                    patch(index, { ratePercent: e.target.value.replace(/[^\d.,]/g, '') })
                  }
                  inputMode="decimal"
                  placeholder="1,99"
                  className="input w-full text-right"
                />
              </Field>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-3">
              <Field label="Parcelas" required>
                <input
                  value={draft.installments}
                  onChange={(e) =>
                    patch(index, { installments: e.target.value.replace(/\D/g, '').slice(0, 3) })
                  }
                  inputMode="numeric"
                  placeholder="24"
                  className="input w-full text-right"
                />
              </Field>
              <Field label="Sistema">
                <select
                  value={draft.system}
                  onChange={(e) => patch(index, { system: e.target.value })}
                  className="input w-full"
                >
                  <option value="price">PRICE (parcela fixa)</option>
                  <option value="sac">SAC (decrescente)</option>
                  <option value="bullet">Bullet (só juros)</option>
                </select>
              </Field>
            </div>

            <div className="mt-2 flex flex-wrap gap-1">
              {QUICK_TERMS.map((n) => (
                <button
                  key={n}
                  onClick={() => patch(index, { installments: String(n) })}
                  className={`rounded-md px-2 py-0.5 text-xs transition ${
                    draft.installments === String(n)
                      ? 'bg-indigo-600 text-white'
                      : 'border border-slate-300 text-slate-500 hover:border-indigo-400 dark:border-slate-700'
                  }`}
                >
                  {n}x
                </button>
              ))}
            </div>

            {/* O que o CET existe para revelar: custo retido na liberação. */}
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Field label="IOF retido" hint="reduz o que entra na conta">
                <input
                  value={draft.iof}
                  onChange={(e) => patch(index, { iof: maskMoneyInput(e.target.value) })}
                  inputMode="numeric"
                  placeholder="0,00"
                  className="input w-full text-right"
                />
              </Field>
              <Field label="TAC e tarifas">
                <input
                  value={draft.fees}
                  onChange={(e) => patch(index, { fees: maskMoneyInput(e.target.value) })}
                  inputMode="numeric"
                  placeholder="0,00"
                  className="input w-full text-right"
                />
              </Field>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {drafts.length < 4 && (
          <button
            onClick={() => setDrafts((prev) => [...prev, { ...EMPTY }])}
            className="rounded-lg border border-slate-300 px-3.5 py-2 text-sm dark:border-slate-700"
          >
            + Proposta
          </button>
        )}
        <button
          onClick={() => simulate.mutate()}
          disabled={!complete || simulate.isPending}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
        >
          {simulate.isPending ? 'Calculando…' : 'Comparar'}
        </button>
        <span className="text-xs text-slate-400">Nada é gravado.</span>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
        >
          <p>{error.message}</p>
          {error.errors && (
            <ul className="mt-1 list-inside list-disc">
              {Object.values(error.errors).map((m, i) => <li key={i}>{m[0]}</li>)}
            </ul>
          )}
        </div>
      )}

      {result && <ComparisonTable result={result} />}
    </div>
  );
}

// -------------------------------------------------------------------

function ComparisonTable({ result }: { result: ComparisonResult }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="border-b border-slate-200 p-3.5 dark:border-slate-800">
        <h2 className="font-medium">Comparação</h2>
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
          Recomendação por {result.criterion}.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
            <tr>
              <th className="px-3 py-2 font-medium">Proposta</th>
              <th className="px-3 py-2 text-right font-medium">Parcela</th>
              <th className="px-3 py-2 text-right font-medium">Prazo</th>
              <th className="px-3 py-2 text-right font-medium">Total pago</th>
              <th className="px-3 py-2 text-right font-medium">Juros</th>
              <th className="px-3 py-2 text-right font-medium">CET a.m.</th>
              <th className="px-3 py-2 text-right font-medium">CET a.a.</th>
              <th className="px-3 py-2 text-right font-medium">Valor presente</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {result.proposals.map((p) => (
              <tr
                key={p.index}
                className={p.is_recommended ? 'bg-emerald-50/60 dark:bg-emerald-950/20' : ''}
              >
                <td className="px-3 py-2">
                  <span className="font-medium">{p.label}</span>
                  {p.is_baseline && (
                    <span className="ml-1.5 text-[10px] uppercase tracking-wide text-slate-400">
                      referência
                    </span>
                  )}
                  {p.is_recommended && (
                    <span className="ml-1.5 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                      menor custo
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right">
                  {formatMoney(p.installment_amount)}
                </td>
                <td className="px-3 py-2 text-right">{p.schedule_length}x</td>
                <td className="whitespace-nowrap px-3 py-2 text-right">{formatMoney(p.total_paid)}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right text-slate-500 dark:text-slate-400">
                  {formatMoney(p.total_interest)}
                </td>
                {/* O CET em destaque: é ele que torna a comparação válida. */}
                <td className="whitespace-nowrap px-3 py-2 text-right font-medium">
                  {formatRate(p.cet_monthly)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right text-slate-500 dark:text-slate-400">
                  {formatRate(p.cet_annual, 1)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right">
                  {formatMoney(p.present_value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="space-y-2 border-t border-slate-200 p-3.5 dark:border-slate-800">
        {result.proposals.map((p) => <Verdict key={p.index} proposal={p} />)}

        <p className="pt-1 text-[11px] text-slate-400">{result.disclaimer}</p>
      </div>
    </div>
  );
}

function Verdict({ proposal }: { proposal: ProposalResult }) {
  if (proposal.is_baseline) return null;

  return (
    <div>
      {/* RN-70 — nunca apresentar "parcela menor" como vantagem isolada. */}
      {proposal.warning ? (
        <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200">
          <strong>{proposal.label}:</strong> {proposal.warning}
        </p>
      ) : (
        <p className="text-sm text-slate-600 dark:text-slate-300">
          <strong>{proposal.label}:</strong> {proposal.verdict}.
        </p>
      )}
    </div>
  );
}
